import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText, formatDateTime, formatMoney, formatQty } from '../api';
import { useAuth } from '../auth';
import { Card, Empty, Loading, Notice, ReasonBadge } from '../components/ui';
import type { Dashboard } from '../types';

function Tile({
  label,
  value,
  hint,
  alert,
}: {
  label: string;
  value: string;
  hint?: string;
  alert?: boolean;
}) {
  return (
    <div className={alert ? 'kpi-tile alert' : 'kpi-tile'}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

function DayChart({ data }: { data: Dashboard['perDay'] }) {
  const max = Math.max(1, ...data.map((d) => Math.max(d.eingang, d.ausgang)));

  return (
    <>
      <div className="spark">
        {data.map((day) => (
          <div className="day" key={day.tag} title={`${day.tag}: +${day.eingang} / -${day.ausgang}`}>
            <div className="in" style={{ height: `${(day.eingang / max) * 45}%` }} />
            <div className="out" style={{ height: `${(day.ausgang / max) * 45}%` }} />
          </div>
        ))}
      </div>
      <div className="legend">
        <span>
          <i style={{ background: 'var(--ok)' }} />
          Eingang
        </span>
        <span>
          <i style={{ background: 'var(--accent)' }} />
          Ausgang
        </span>
        <span className="spacer" style={{ flex: 1 }} />
        <span>letzte 14 Tage</span>
      </div>
    </>
  );
}

export function DashboardPage() {
  const { ownerId } = useAuth();
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null);
    api<Dashboard>('/api/dashboard', { query: { ownerId } })
      .then(setData)
      .catch((err) => setError(errorText(err)));
  }, [ownerId]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!data) return <Loading />;

  const { kpi } = data;
  const utilisation =
    kpi.locationsTotal > 0 ? Math.round((kpi.locationsOccupied / kpi.locationsTotal) * 100) : 0;

  return (
    <>
      {kpi.failedJobs > 0 && (
        <Notice kind="error">
          {kpi.failedJobs} Hintergrundauftrag/-aufträge stehen auf Fehler und müssen geprüft werden.
        </Notice>
      )}

      <div className="grid kpi" style={{ marginBottom: 16 }}>
        <Tile label="Bestand gesamt" value={formatQty(kpi.stockUnits)} hint="Einheiten über alle Plätze" />
        <Tile
          label="Bestandswert eigene Ware"
          value={formatMoney(kpi.stockValueCents)}
          hint="zu Einkaufspreisen"
        />
        <Tile
          label="Lagerplätze belegt"
          value={`${kpi.locationsOccupied} / ${kpi.locationsTotal}`}
          hint={`${utilisation} % Auslastung`}
        />
        <Tile label="Bewegungen heute" value={String(kpi.movementsToday)} />
        <Tile label="Artikel aktiv" value={String(kpi.articles)} hint={`${kpi.owners} Eigentümer`} />
        <Tile
          label="Unter Mindestbestand"
          value={String(kpi.belowMin)}
          alert={kpi.belowMin > 0}
          hint={kpi.openCounts > 0 ? `${kpi.openCounts} Inventur(en) offen` : undefined}
        />
      </div>

      <div className="grid two">
        <Card title="Warenfluss">
          <DayChart data={data.perDay} />
        </Card>

        <Card title="Bestand je Eigentümer">
          {data.topOwners.length === 0 ? (
            <Empty>Noch kein Bestand gebucht.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Eigentümer</th>
                  <th className="num">Artikel</th>
                  <th className="num">Einheiten</th>
                </tr>
              </thead>
              <tbody>
                {data.topOwners.map((owner) => (
                  <tr key={owner.owner_id}>
                    <td>{owner.owner_name}</td>
                    <td className="num">{owner.articles}</td>
                    <td className="num">{formatQty(owner.units)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div className="grid two">
        <Card
          title="Letzte Bewegungen"
          actions={<Link to="/bewegungen">alle ansehen</Link>}
          padded={false}
        >
          {data.recentMovements.length === 0 ? (
            <Empty>Noch nichts gebucht.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Art</th>
                  <th>Artikel</th>
                  <th className="num">Menge</th>
                  <th>Weg</th>
                </tr>
              </thead>
              <tbody>
                {data.recentMovements.map((movement) => (
                  <tr key={movement.id}>
                    <td className="small muted">{formatDateTime(movement.booked_at)}</td>
                    <td>
                      <ReasonBadge reason={movement.reason} />
                    </td>
                    <td>
                      <span className="mono">{movement.sku}</span> {movement.article_name}
                    </td>
                    <td className="num">{formatQty(movement.qty)}</td>
                    <td className="small mono">
                      {movement.from_code ?? '—'} → {movement.to_code ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Unter Mindestbestand" actions={<Link to="/artikel">Artikel</Link>} padded={false}>
          {data.alerts.length === 0 ? (
            <Empty>Alle Artikel über dem Mindestbestand.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th className="num">Bestand</th>
                  <th className="num">Minimum</th>
                </tr>
              </thead>
              <tbody>
                {data.alerts.map((alert) => (
                  <tr key={alert.sku + alert.owner_name}>
                    <td>
                      <span className="mono">{alert.sku}</span> {alert.article_name}
                    </td>
                    <td className="small">{alert.owner_name}</td>
                    <td className="num" style={{ color: 'var(--bad)' }}>
                      {formatQty(alert.stock)}
                    </td>
                    <td className="num muted">{formatQty(alert.min_stock)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
